import { Dispatch } from 'redux';
import AdAudienceController from '../controllers/AdAudienceController';
import adAudiencesHandler from '../errorHandlers/ad-audience-handler';
import commonHandler from '../errorHandlers/common-handler';
import { StoreState } from '../store-interfaces';
import CrudActionContainer from './CrudActionContainer';
import { getActionHookBuilder } from './action-hook-builder';
import { AdAudienceCreateUpdatePayload } from '../model/AdAudienceModel';
import { getErrorJson } from '@mm/utils/src/fetch-utils';

const NOT_FB_PAGE_ERROR_CODE = 1020;

class AdAudienceCRUDActions extends CrudActionContainer {
  resetAction: string;

  constructor() {
    super('AD_AUDIENCES');
  }

  assignAdAudience(facebookPageId: number, audienceId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const res = await AdAudienceController.assignAdAudience(
          facebookPageId,
          audienceId,
        );

        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        return adAudiencesHandler(dispatch, this)(e);
      }
    };
  }

  createAdAudience(
    facebookPageId: number,
    adAudience: Partial<AdAudienceCreateUpdatePayload>,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));

        const res = await AdAudienceController.createAdAudience(
          facebookPageId,
          adAudience,
        );

        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        return adAudiencesHandler(dispatch, this)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  updateAdAudienceByRemoteId(
    facebookPageId: number,
    adAudienceRemoteId: number | string,
    update: Partial<AdAudienceCreateUpdatePayload>,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));

        const res = await AdAudienceController.updateAdAudienceByRemoteId(
          facebookPageId,
          adAudienceRemoteId,
          update,
        );

        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        return adAudiencesHandler(dispatch, this)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieve(facebookPageId: number, audienceId: number, remoteId: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAction));

        const res = await AdAudienceController.getAudience(
          facebookPageId,
          audienceId,
          remoteId,
        );
        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveAudienceByRemoteId(
    facebookPageId: number,
    adAudienceRemoteId: string | number,
    catchManually?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAction));

        const res = await AdAudienceController.retrieveAudienceByRemoteId(
          facebookPageId,
          adAudienceRemoteId,
        );
        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        if (catchManually) {
          throw e;
        }

        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  syncAudienceByRemoteId(
    facebookPageId: number,
    adAudienceRemoteId: string | number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(''));

        const res = await AdAudienceController.syncAudienceByRemoteId(
          facebookPageId,
          adAudienceRemoteId,
        );
        dispatch(this.retrieved(res));
        return res;
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  deleteAudienceByRemoteId(
    facebookPageId: number,
    adAudienceId: string | number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.deleteAction));
        const res = await AdAudienceController.deleteAudienceByRemoteId(
          facebookPageId,
          adAudienceId,
        );
        dispatch(this.deleteLocal(res.adAudience));
        return res;
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveAll(facebookPageId: number, adAccountId?: number | string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const res = await AdAudienceController.retrieveAudiences(
          facebookPageId,
          adAccountId,
        );
        dispatch(this.retrievedAll(res.adAudiences));
        return res;
      } catch (e) {
        const errorJson = await getErrorJson(e);

        if (errorJson.error_code === NOT_FB_PAGE_ERROR_CODE) {
          return;
        }

        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }
}

const adAudienceCRUDActions = new AdAudienceCRUDActions();

export const useAdAudienceCRUDActions = getActionHookBuilder(
  adAudienceCRUDActions,
  [
    'retrieveAll',
    'createAdAudience',
    'updateAdAudienceByRemoteId',
    'assignAdAudience',
    'retrieveAudienceByRemoteId',
    'syncAudienceByRemoteId',
    'deleteAudienceByRemoteId',
  ],
);

export default adAudienceCRUDActions;
